import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Col, Row } from 'react-grid-system'
import textsData from '@/textData/AddBudgetFormTexts.json'
import { useErrorHandlerContext, useUserLanguage } from '@/customHooks'
import { CreateBudgetInput, useGetSuppliersMinimalQuery } from '@/graphql/generated'
import {
  NormalFormNumberField,
  NormalFormSelectionField,
  NormalFormTextField,
} from '../molecules'
import { LoadingSpinner } from '../atoms'
import { capitalizeFirstLetter } from '@/helpers'

type AddBudgetFormProps = {
  defaultBudgetData: CreateBudgetInput | null
  onChange: (budgetData: CreateBudgetInput) => void
}

const emptyData: CreateBudgetInput = {
  budgetCost: '',
  budgetEarning: '',
  observation: '',
  subject: '',
}

export const  AddBudgetFormControlled = memo(({ defaultBudgetData, onChange }: AddBudgetFormProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [initialBudgetData, setBudgetData] = useState<CreateBudgetInput>(defaultBudgetData || emptyData)
  const budgetDataRef = useRef<CreateBudgetInput>(defaultBudgetData || emptyData)
  const [suppliersToSelectField, setSuppliersToSelectField] = useState<{name: string; value: string}[]>([])

  const { open: errorHandle } = useErrorHandlerContext()
  const {data, loading, error} = useGetSuppliersMinimalQuery()

  useEffect(() => {
    if (!data?.suppliers?.items) return

    setSuppliersToSelectField(data.suppliers.items
      .filter((supplier) => supplier.isActive)
      .map((supplier) => ({
        name: capitalizeFirstLetter(`${supplier.firstName} ${supplier.lastName}`),
        value: supplier.user.id
      }))) 
  }, [data])

  useEffect(() => {
    if (!defaultBudgetData) return

    budgetDataRef.current = defaultBudgetData
    setBudgetData(defaultBudgetData)
  }, [defaultBudgetData])

  const onFieldChangeHandle = useCallback((field: keyof CreateBudgetInput, value: string | null) => {
    if (!value) return

    budgetDataRef.current = {
      ...budgetDataRef.current,
      [field]: value
    }

    onChange(budgetDataRef.current)
  }, [onChange])

  if (error) errorHandle(error)

  return (
    <>
      <Row>
        <Col md={6}>
          <NormalFormTextField
            name="subject"
            label={texts.subject.label}
            value={initialBudgetData.subject}
            placeholder={texts.subject.placeholder}
            onChange={(value) => onFieldChangeHandle('subject', value)}
          />
        </Col>
        <Col md={6}>
          {
            loading
            ? <LoadingSpinner sm />
            : (
              <NormalFormSelectionField
                options={suppliersToSelectField}
                name="suplier"
                value={initialBudgetData.supplierId}
                label={texts.selectSupplier.label}
                onChange={(value) => onFieldChangeHandle('supplierId', value)}
              />
            )
          }
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <NormalFormNumberField
            name="budgetCost"
            label={texts.budgetCost.label}
            value={initialBudgetData.budgetCost}
            onChange={(value) => onFieldChangeHandle('budgetCost', value)}
          />
        </Col>
        <Col md={6}>
          <NormalFormNumberField
            name="budgetEarning"
            label={texts.budgetEarning.label}
            value={initialBudgetData.budgetEarning ?? undefined}
            onChange={(value) => onFieldChangeHandle('budgetEarning', value)}
          />
        </Col>
        <Col xs={12}>
          <NormalFormTextField
            name="observation"
            label={texts.observation.label}
            placeholder={texts.observation.placeholder}
            value={initialBudgetData.observation}
            onChange={(value) => onFieldChangeHandle('observation', value)}
          />
        </Col>
      </Row>
    </>
  )
})
